import React, { useState, useRef, useEffect } from 'react';
import Header from './Header.jsx';
import { domain } from './Domain.jsx';
import useFetch from './useFetch.jsx';
import useApiRequest from './useApiRequest.jsx';
import { useNavigate } from 'react-router-dom';
import { useLoadingContext } from './LoadingContext.jsx';
import { saveToLocalStorage } from './EncryptDecrypt.jsx';
import notify from './Nots.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Edit, Trash2, Calendar, MapPin, Users, Eye, 
    Plus, Search, Filter, MoreVertical, AlertCircle,
    CheckCircle, Clock, Star, TrendingUp, CalendarDays
} from 'lucide-react';

const EventCard = ({ event, detailed, onEdit, onDelete, index }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [showActions, setShowActions] = useState(false);

    const truncateString = (str, maxLength = 120) => {
        if (!str || str.length <= maxLength) return str;
        let truncated = str.substring(0, maxLength);
        const lastSpaceIndex = truncated.lastIndexOf(' ');
        if (lastSpaceIndex > -1) {
            truncated = truncated.substring(0, lastSpaceIndex);
        }
        return truncated + " ...";
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'TBD';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const getStatusColor = (status) => {
        return status ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
    };

    const getStatusIcon = (status) => {
        return status ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />;
    };

    const handleImageLoad = () => {
        setImageLoaded(true);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageError(true);
        setImageLoaded(true);
    };

    if (!detailed) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden"
            >
                <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <AlertCircle className="w-16 h-16 text-gray-400" />
                </div>
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Missing Event Details</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(event.status)}`}>
                            {getStatusIcon(event.status)}
                            {event.status ? 'Completed' : 'Incomplete'}
                        </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-4 italic">
                        "Important details for this event are missing. Please review and update the necessary information."
                    </p>
                    <div className="flex gap-2">
                        <button 
                            onClick={() => onEdit(event)}
                            className="flex-1 bg-orange-500 hover:bg-orange-600 !text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                        >
                            <Edit className="w-4 h-4" />
                            Edit
                        </button>
                        <button 
                            onClick={() => onDelete(event)}
                            className="flex-1 bg-red-500 hover:bg-red-600 !text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete
                        </button>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group"
        >
            <div className="relative h-48 overflow-hidden">
                {!imageLoaded && (
                    <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                        <div className="w-8 h-8 border-4 border-gray-300 border-t-orange-500 rounded-full animate-spin"></div>
                    </div>
                )}
                <img 
                    src={imageError ? "https://via.placeholder.com/400x200?text=Image+Not+Available" : domain + detailed.banner}
                    alt={detailed.event_plot_name} 
                    className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                />
                <div className="absolute top-3 right-3">
                    <button
                        onClick={() => setShowActions(!showActions)}
                        className="p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-white transition-all duration-200"
                    >
                        <MoreVertical className="w-4 h-4 text-gray-600" />
                    </button>
                    <AnimatePresence>
                        {showActions && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                                className="absolute right-0 top-12 bg-white rounded-lg shadow-xl border border-gray-100 py-2 min-w-[120px] z-10"
                            >
                                <button
                                    onClick={() => {
                                        onEdit(detailed);
                                        setShowActions(false);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                                >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => {
                                        onDelete(detailed);
                                        setShowActions(false);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <div className="absolute bottom-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(detailed.organiser.status)}`}>
                        {getStatusIcon(detailed.organiser.status)}
                        {detailed.organiser.status ? 'Completed' : 'Incomplete'}
                    </span>
                </div>
            </div>
            
            <div className="p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                    {detailed.event_plot_name}
                </h3>
                
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {truncateString(detailed.event_description)}
                </p>
                
                <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin className="w-4 h-4" />
                        <span>{detailed.venue_name || 'Venue TBD'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(detailed.start_date_event)}</span>
                    </div>
                    {detailed.event_capacity && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Users className="w-4 h-4" />
                            <span>Capacity: {detailed.event_capacity}</span>
                        </div>
                    )}
                </div>
                
                <div className="flex gap-2">
                    <button 
                        onClick={() => onEdit(detailed)}
                        className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 !text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    >
                        <Edit className="w-4 h-4" />
                        Edit Event
                    </button>
                    <button 
                        onClick={() => onDelete(detailed)}
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

const EventList = ({ dataProfiles, events, onEdit, onDelete }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const itemsPerPage = 6;

    // Filter and search events
    const filteredEvents = dataProfiles?.filter(event => {
        const detailed = events?.find(item => item.organiser.id === event.id);
        const matchesSearch = detailed?.event_plot_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             event.organiser_name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || 
                             (filterStatus === 'completed' && event.status) ||
                             (filterStatus === 'incomplete' && !event.status);
        return matchesSearch && matchesStatus;
    }) || [];

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEvents = filteredEvents.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const resetFilters = () => {
        setSearchTerm('');
        setFilterStatus('all');
        setCurrentPage(1);
    };

    return (
        <div className="space-y-6">
            {/* Search and Filter Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search events..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                            </div>
                    <div className="flex gap-2">
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                            <option value="all">All Events</option>
                            <option value="completed">Completed</option>
                            <option value="incomplete">Incomplete</option>
                        </select>
                        <button
                            onClick={resetFilters}
                            className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 flex items-center gap-2"
                        >
                            <Filter className="w-4 h-4" />
                            Reset
                                </button>
                            </div>
                        </div>
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between">
                <p className="text-gray-600">
                    Showing {filteredEvents.length} of {dataProfiles?.length || 0} events
                </p>
                {filteredEvents.length === 0 && (
                    <button
                        onClick={resetFilters}
                        className="text-orange-500 hover:text-orange-600 font-medium"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {/* Events Grid */}
            {currentEvents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentEvents.map((event, index) => {
                        const detailed = events?.find(item => item.organiser.id === event.id);
                        return (
                            <EventCard
                                key={event.id || index}
                                event={event}
                                detailed={detailed}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                index={index}
                            />
                );
            })}
                </div>
            ) : (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-12"
                >
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CalendarDays className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No events found</h3>
                    <p className="text-sm text-gray-600 mb-6">
                        {searchTerm || filterStatus !== 'all' 
                            ? 'Try adjusting your search or filters'
                            : 'You haven\'t created any events yet'
                        }
                    </p>
                    {!searchTerm && filterStatus === 'all' && (
                        <button className="bg-orange-500 hover:bg-orange-600 !text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 mx-auto">
                            <Plus className="w-5 h-5" />
                            Create Your First Event
                        </button>
                    )}
                </motion.div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                        Previous
                    </button>
                    
                    {[...Array(totalPages).keys()].map(page => {
                        const pageNum = page + 1;
                        const isCurrent = currentPage === pageNum;
                        const isNearCurrent = Math.abs(currentPage - pageNum) <= 1;
                        
                        if (isNearCurrent || pageNum === 1 || pageNum === totalPages) {
                            return (
                        <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                                        isCurrent 
                                            ? 'bg-orange-500 !text-white' 
                                            : 'border border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {pageNum}
                        </button>
                            );
                        } else if (pageNum === 2 && currentPage > 3) {
                            return <span key={pageNum} className="px-2">...</span>;
                        } else if (pageNum === totalPages - 1 && currentPage < totalPages - 2) {
                            return <span key={pageNum} className="px-2">...</span>;
                        }
                        return null;
                    })}
                    
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

const MyEvents = () => {
    const navigate = useNavigate();
    const { deleteRequest } = useApiRequest();
    const { data: dataEvents, loading: loadingEvents, refresh, setRefresh } = useFetch(`${domain}/event-details`);
    const { data: dataProfiles, loading: loadingProfiles, refresh: refreshProfiles, setRefresh: setRefreshProfiles } = useFetch(`${domain}/my-events`);
    const { loading, setLoading } = useLoadingContext();
    const [deleteLoading, setDeleteLoading] = useState(null);

    useEffect(() => {
        if (loadingEvents && loadingProfiles) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [loadingEvents, loadingProfiles, setLoading]);

    const handleEdit = async (event) => {
        localStorage.setItem("edit", true);
        saveToLocalStorage("eventReg", event.slug);
        navigate("/create-plot");
    };

    const handleDelete = async (event) => {
        if (window.confirm("Are you sure you want to delete this event? This action cannot be undone.")) {
            setDeleteLoading(event.id);
            try {
            const res = await deleteRequest(`${domain}/event-details/${event.slug}`);
            if (res) {
                setRefresh(!refresh);
                setRefreshProfiles(!refreshProfiles);
                    notify("Event deleted successfully", "success");
                }
            } catch (error) {
                notify("Failed to delete event", "error");
            } finally {
                setDeleteLoading(null);
            }
        }
    };

    const handleCreateNew = () => {
        navigate("/create-plot");
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-teal-50">
            <Header />
            <div className="container mx-auto px-4 py-8">
                {/* Header Section */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-lg font-bold text-gray-800 mb-2">My Events</h1>
                            <p className="text-sm text-gray-600">
                                Manage and organize all your events in one place
                            </p>
                        </div>
                        <button
                            onClick={handleCreateNew}
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
                        >
                            <Plus className="w-5 h-5" />
                            Create New Event
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                {dataProfiles && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Events</p>
                                    <p className="text-sm font-bold text-gray-800">{dataProfiles.length}</p>
                                </div>
                                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <Calendar className="w-6 h-6 text-orange-600" />
                                </div>
                            </div>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Completed</p>
                                    <p className="text-sm font-bold text-teal-600">
                                        {dataProfiles.filter(event => event.status).length}
                                    </p>
                                </div>
                                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                                    <CheckCircle className="w-6 h-6 text-teal-600" />
                                </div>
                            </div>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">In Progress</p>
                                    <p className="text-sm font-bold text-orange-600">
                                        {dataProfiles.filter(event => !event.status).length}
                                    </p>
                                </div>
                                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <Clock className="w-6 h-6 text-orange-600" />
                                </div>
                            </div>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">This Month</p>
                                    <p className="text-sm font-bold text-teal-600">
                                        {dataProfiles.filter(event => {
                                            const eventDate = new Date(event.created_at);
                                            const now = new Date();
                                            return eventDate.getMonth() === now.getMonth() && 
                                                   eventDate.getFullYear() === now.getFullYear();
                                        }).length}
                                    </p>
                                </div>
                                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                                    <TrendingUp className="w-6 h-6 text-teal-600" />
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* Events List */}
                {dataProfiles ? (
                    <EventList 
                        dataProfiles={dataProfiles} 
                        events={dataEvents} 
                        onEdit={handleEdit} 
                        onDelete={handleDelete} 
                    />
                ) : (
                    <div className="text-center py-12">
                        <div className="w-16 h-16 border-4 border-gray-300 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Loading your events...</h3>
                        <p className="text-sm text-gray-600">Please wait while we fetch your events</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyEvents;